import axios from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDeleteProductionDeclarationDocument } from '@/services/apis/mutations/production-declaration/use-delete-production-declaration-document';
import type { ProductionDeclarationCommodityOriginDocument } from '@/types/core/purchase-order/production-declaration-commodity-origin-document';

type UseProductionDeclarationMaterialAdditionalFilesDeleteDialogParams = {
  commodityOriginId: string;
  materialId: string;
  productionDeclarationId: string;
  token?: string;
};

export function useProductionDeclarationMaterialAdditionalFilesDeleteDialog({
  commodityOriginId,
  materialId,
  productionDeclarationId,
  token,
}: UseProductionDeclarationMaterialAdditionalFilesDeleteDialogParams) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const [documentToDelete, setDocumentToDelete] =
    useState<ProductionDeclarationCommodityOriginDocument | null>(null);
  const { isPending, mutate: deleteDocument } =
    useDeleteProductionDeclarationDocument();

  const openDeleteDialog = useCallback(
    (document: ProductionDeclarationCommodityOriginDocument) => {
      setDocumentToDelete(document);
    },
    [],
  );

  const handleDeleteDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isPending || nextOpen) {
        return;
      }

      setDocumentToDelete(null);
    },
    [isPending],
  );

  const handleDelete = useCallback(() => {
    if (!documentToDelete) {
      return;
    }

    deleteDocument(
      {
        commodityOriginId,
        documentId: documentToDelete.id,
        materialId,
        productionDeclarationId,
        token,
      },
      {
        onSuccess: () => {
          toast.success(
            t(
              'materialCommodityOrigins.cards.additionalFiles.actions.deleteSuccess',
            ),
          );
          setDocumentToDelete(null);
        },
        onError: (error) => {
          const description = axios.isAxiosError(error)
            ? (error.response?.data?.message ??
              error.response?.data?.detail ??
              t(
                'materialCommodityOrigins.cards.additionalFiles.actions.deleteError',
              ))
            : t(
                'materialCommodityOrigins.cards.additionalFiles.actions.deleteError',
              );

          toast.error(tCommon('error'), {
            description,
          });
        },
      },
    );
  }, [
    commodityOriginId,
    deleteDocument,
    documentToDelete,
    materialId,
    productionDeclarationId,
    t,
    tCommon,
    token,
  ]);

  return {
    handleDelete,
    handleDeleteDialogOpenChange,
    isDeleteDialogOpen: documentToDelete !== null,
    isPending,
    openDeleteDialog,
  };
}
